import { useRef, useEffect, useCallback } from 'react'

// Module-level singleton: only one row can be open at a time
let currentOpenClose = null

const DIRECTION_LOCK_THRESHOLD = 10 // px to determine swipe vs scroll
const SNAP_THRESHOLD = 0.3 // fraction of action width to snap open
const MAX_OVERSWIPE = 20 // px beyond full action width

function SwipeableRow({ children, actions = [], actionWidth = 52, disabled = false }) {
  const isTouchDevice = useRef(false)
  const innerRef = useRef(null)
  const wrapperRef = useRef(null)
  const startX = useRef(0)
  const startY = useRef(0)
  const startOffset = useRef(0)
  const currentOffset = useRef(0)
  const directionLocked = useRef(null)
  const suppressClick = useRef(false)
  const isOpen = useRef(false)

  const totalActionWidth = actions.length * actionWidth

  useEffect(() => {
    isTouchDevice.current =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none)').matches
  }, [])

  const setTransform = useCallback((x, animate = false) => {
    const el = innerRef.current
    if (!el) return
    if (animate) {
      el.classList.add('swipeable-row-inner--animating')
    } else {
      el.classList.remove('swipeable-row-inner--animating')
    }
    el.style.transform = `translateX(${x}px)`
    currentOffset.current = x
  }, [])

  const snapClose = useCallback(() => {
    setTransform(0, true)
    isOpen.current = false
    if (currentOpenClose === closeThisRow.current) {
      currentOpenClose = null
    }
  }, [setTransform])

  const closeThisRow = useRef(null)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- ref mutation is intentional: keep ref in sync with latest snapClose callback
    closeThisRow.current = snapClose
  }, [snapClose])

  // Clean up on unmount: if this row is the open one, clear the singleton
  useEffect(() => {
    return () => {
      if (currentOpenClose === closeThisRow.current) {
        currentOpenClose = null
      }
    }
  }, [])

  function handleTouchStart(e) {
    if (disabled || !isTouchDevice.current) return
    const touch = e.touches[0]
    startX.current = touch.clientX
    startY.current = touch.clientY
    startOffset.current = currentOffset.current
    directionLocked.current = null

    // Remove animation class for immediate response
    if (innerRef.current) {
      innerRef.current.classList.remove('swipeable-row-inner--animating')
    }
  }

  function handleTouchMove(e) {
    if (disabled || !isTouchDevice.current) return
    const touch = e.touches[0]
    const deltaX = touch.clientX - startX.current
    const deltaY = touch.clientY - startY.current

    // Determine direction intent
    if (directionLocked.current === null) {
      if (Math.abs(deltaX) + Math.abs(deltaY) < DIRECTION_LOCK_THRESHOLD) return
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        directionLocked.current = 'horizontal'
      } else {
        directionLocked.current = 'vertical'
        return
      }
    }

    if (directionLocked.current !== 'horizontal') return

    // Prevent vertical scroll / pull-to-refresh while swiping horizontally
    e.preventDefault()

    let newOffset = startOffset.current + deltaX

    // Clamp: don't allow swiping right past 0
    if (newOffset > 0) newOffset = 0

    // Apply resistance past the full action width
    const fullOpen = -totalActionWidth
    if (newOffset < fullOpen) {
      const over = fullOpen - newOffset
      const dampened = Math.min(MAX_OVERSWIPE, over * 0.3)
      newOffset = fullOpen - dampened
    }

    setTransform(newOffset, false)
  }

  function handleTouchEnd() {
    if (disabled || !isTouchDevice.current) return
    if (directionLocked.current !== 'horizontal') return

    const offset = currentOffset.current
    const threshold = totalActionWidth * SNAP_THRESHOLD

    if (Math.abs(offset) > threshold) {
      // Snap open
      setTransform(-totalActionWidth, true)
      isOpen.current = true
      suppressClick.current = true

      // Close any other open row, then register this one
      if (currentOpenClose && currentOpenClose !== closeThisRow.current) {
        currentOpenClose()
      }
      currentOpenClose = closeThisRow.current
    } else {
      // Snap closed
      snapClose()
      if (Math.abs(offset) > 2) {
        suppressClick.current = true
      }
    }

    directionLocked.current = null
  }

  function handleClickCapture(e) {
    if (suppressClick.current) {
      e.stopPropagation()
      e.preventDefault()
      suppressClick.current = false
    }
  }

  function handleActionClick(action, e) {
    e.stopPropagation()
    action.onClick(e)
    snapClose()
  }

  return (
    <div
      ref={wrapperRef}
      className="swipeable-row"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div ref={innerRef} className="swipeable-row-inner" onClickCapture={handleClickCapture}>
        {children}
      </div>
      {actions.length > 0 && (
        <div
          className="swipeable-row-actions"
          style={{ width: `${totalActionWidth}px` }}
        >
          {actions.map((action) => (
            <button
              key={action.key}
              className={`swipeable-row-action swipeable-row-action--${action.variant || 'default'}`}
              style={{ width: `${actionWidth}px` }}
              onClick={(e) => handleActionClick(action, e)}
              title={action.label || ''}
            >
              {action.icon}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default SwipeableRow
